import { forwardRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Slide,
} from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import { useTranslation } from 'react-i18next';

// 上滑進入、下滑離開
const SlideUpTransition = forwardRef(function SlideUpTransition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CYAN = '#5BB8D4';
const CYAN_DARK = '#3FA0C2';

interface TermsSection {
  heading: string;
  body: string;
}
interface TermsPart {
  title: string;
  intro: string;
  sections: TermsSection[];
}

interface Props {
  open: boolean;
  onAgree: () => void;
  onDecline: () => void;
}

/**
 * 登入後的同意 gate：顯示完整「服務條款＋隱私權政策」全文（可捲動），
 * 同意才能繼續、不同意則登出。點背景／ESC 不關閉（硬性 gate）。
 */
export default function TermsConsentDialog({ open, onAgree, onDecline }: Props) {
  const { t } = useTranslation('terms');
  const parts = t('parts', { returnObjects: true }) as TermsPart[];

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onDecline();
      }}
      disableEscapeKeyDown
      maxWidth={false}
      TransitionComponent={SlideUpTransition}
      transitionDuration={{ enter: 320, exit: 240 }}
      PaperProps={{
        sx: {
          width: 'calc(100vw - 32px)',
          maxWidth: 640,
          maxHeight: '85vh',
          m: 0,
          borderRadius: '20px',
          overflow: 'hidden',
          bgcolor: '#fff',
        },
      }}
    >
      {/* 標題列（青藍底） */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          background: `linear-gradient(135deg, ${CYAN} 0%, ${CYAN_DARK} 100%)`,
          color: 'common.white',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t('title')}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
          {t('consentIntro')}
        </Typography>
      </Box>

      {/* 全文（可捲動，白底黑字） */}
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {parts.map((part, index) => (
          <Box key={part.title} sx={{ mb: 3 }}>
            {index > 0 && <Divider sx={{ mb: 3 }} />}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: '#1a1a1a' }}>
              {part.title}
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.5, color: 'rgba(0,0,0,0.6)', mb: 2 }}>
              {part.intro}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {part.sections.map((section) => (
                <Box key={section.heading}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#1a1a1a' }}>
                    {section.heading}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ lineHeight: 1.5, color: 'rgba(0,0,0,0.6)', whiteSpace: 'pre-line' }}
                  >
                    {section.body}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
        <Button
          onClick={onDecline}
          sx={{ textTransform: 'none', borderRadius: '33px', px: 2, color: CYAN_DARK }}
        >
          {t('decline')}
        </Button>
        <Button
          variant="contained"
          onClick={onAgree}
          sx={{
            textTransform: 'none',
            borderRadius: '33px',
            px: 3,
            fontWeight: 700,
            bgcolor: CYAN,
            '&:hover': { bgcolor: CYAN_DARK },
          }}
        >
          {t('agree')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
